import { MutableRefObject, useEffect, useRef, useState, useCallback } from 'react';

type HorizontalSwipeRecognizerOptions = {
    onLeftSwipe?: () => void;
    onRightSwipe?: () => void;
};

type HorizontalSwipeRecognizerReturn = {
    ref: MutableRefObject<HTMLDivElement | undefined>;
};

function useHorizontalSwipeRecognizer(options: HorizontalSwipeRecognizerOptions): HorizontalSwipeRecognizerReturn {
    const ref = useRef<HTMLDivElement>();
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);

    const minSwipeDistance = 50;

    const onTouchStart = useCallback((e: TouchEvent) => {
        setTouchEnd(null); // Reset touchEnd
        setTouchStart(e.targetTouches[0].clientX);
    }, []);

    const onTouchMove = useCallback((e: TouchEvent) => {
        setTouchEnd(e.targetTouches[0].clientX);
    }, []);

    const onTouchEnd = useCallback(() => {
        if (touchStart === null || touchEnd === null) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            options?.onLeftSwipe();
        } else if (isRightSwipe) {
            options?.onRightSwipe();
        }
    }, [touchStart, touchEnd, options, minSwipeDistance]);

    useEffect(() => {
        const element = ref.current;
        if (!element) return;

        // Attach event listeners
        element.addEventListener('touchstart', onTouchStart);
        element.addEventListener('touchmove', onTouchMove);
        element.addEventListener('touchend', onTouchEnd);

        // Cleanup event listeners on unmount
        return () => {
            element.removeEventListener('touchstart', onTouchStart);
            element.removeEventListener('touchmove', onTouchMove);
            element.removeEventListener('touchend', onTouchEnd);
        };
    }, [onTouchStart, onTouchMove, onTouchEnd]);

    return {
        ref
    };
}

export default useHorizontalSwipeRecognizer;
